import { useRef, type ReactNode } from 'react';
import { Icon } from '@capsico/ui';
import { Button, Notice, Sheet } from './ui';

/*
 * Ratings + reviews kit (W17, capsico-customer-app-ui): rate sheet, star picker, dish like chips,
 * photo tiles, rating summary with the hygiene badge, review cards and the "Rate your orders"
 * strip. Presentation only — every fetch, cache rule and draft lives in hooks/useReviews.ts.
 * Cards have fixed section heights (see hooks/reviewRules.ts) so long lists can be windowed.
 */

/** Thumbs up / down line glyph (24 grid, same stroke as the shared icon set). */
export function Thumb({ down = false, size = 16 }: { down?: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={down ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path d="M7 11v9H4v-9h3zM7 11l4-7a2 2 0 0 1 2.5 2.4L12.8 10H18a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 16.8 20H7" />
    </svg>
  );
}

/** Small read-only star row ("★★★★☆"), amber filled stars. */
export function StarRow({
  value,
  label,
  size = 14,
}: {
  value: number;
  label: string;
  size?: number;
}) {
  return (
    <span role="img" aria-label={label} className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= value ? 'text-warning' : 'text-control'}>
          <Icon name="star" size={size} />
        </span>
      ))}
    </span>
  );
}

/** 1–5 tap stars with the meaning of the chosen value under them. */
export function StarPicker({
  title,
  value,
  onChange,
  caption,
  starLabel,
}: {
  title: string;
  value: number;
  onChange: (stars: number) => void;
  caption: string | null;
  starLabel: (n: number) => string;
}) {
  return (
    <section>
      <p className="text-sm font-semibold text-ink">{title}</p>
      <div role="radiogroup" aria-label={title} className="mt-2 flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={starLabel(n)}
            onClick={() => onChange(n)}
            className={`inline-flex size-11 items-center justify-center rounded-full transition-colors ${
              n <= value
                ? 'bg-brand text-on-brand'
                : 'bg-surface text-ink-3 shadow-[inset_0_0_0_1px_var(--color-control)]'
            }`}
          >
            <Icon name="star" size={20} />
          </button>
        ))}
      </div>
      <p className="mt-1.5 h-4 text-caption font-medium text-ink-2">{caption ?? ''}</p>
    </section>
  );
}

/** One dish with like / unlike chips (exclusive; tapping the active chip clears it). */
export function DishLikeRow({
  name,
  liked,
  unliked,
  likeLabel,
  unlikeLabel,
  onLike,
  onUnlike,
}: {
  name: string;
  liked: boolean;
  unliked: boolean;
  likeLabel: string;
  unlikeLabel: string;
  onLike: () => void;
  onUnlike: () => void;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <p className="min-w-0 flex-1 truncate text-sm text-ink">{name}</p>
      <button
        type="button"
        aria-pressed={liked}
        aria-label={likeLabel}
        onClick={onLike}
        className={`inline-flex size-9 items-center justify-center rounded-full ${
          liked
            ? 'bg-positive-bg text-positive shadow-[inset_0_0_0_1.5px_var(--color-positive)]'
            : 'bg-surface text-ink-2 shadow-[inset_0_0_0_1px_var(--color-control)]'
        }`}
      >
        <Thumb />
      </button>
      <button
        type="button"
        aria-pressed={unliked}
        aria-label={unlikeLabel}
        onClick={onUnlike}
        className={`inline-flex size-9 items-center justify-center rounded-full ${
          unliked
            ? 'bg-negative-bg text-negative shadow-[inset_0_0_0_1.5px_var(--color-negative)]'
            : 'bg-surface text-ink-2 shadow-[inset_0_0_0_1px_var(--color-control)]'
        }`}
      >
        <Thumb down />
      </button>
    </div>
  );
}

export interface PhotoTileView {
  key: string;
  preview: string;
  progress: number | null;
  failed: boolean;
}

/** Up to 3 photo tiles: preview + upload bar + remove ×, then a dashed add tile. */
export function ReviewPhotoTiles({
  slots,
  canAdd,
  labels,
  onFile,
  onRemove,
}: {
  slots: PhotoTileView[];
  canAdd: boolean;
  labels: {
    title: string;
    hint: string;
    add: string;
    remove: string;
    uploading: (percent: number) => string;
    failed: string;
  };
  onFile: (file: File) => void;
  onRemove: (key: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <section>
      <p className="text-sm font-semibold text-ink">{labels.title}</p>
      <p className="text-xs text-ink-3">{labels.hint}</p>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
      <div className="mt-2 flex gap-2">
        {slots.map((slot) => (
          <div key={slot.key} className="relative size-20 overflow-hidden rounded-xl bg-tile">
            <img src={slot.preview} alt="" className="size-full object-cover" />
            {slot.progress !== null && (
              <div className="absolute inset-x-0 bottom-0 bg-scrim px-1.5 py-1">
                <p className="text-micro text-white tabular">{labels.uploading(slot.progress)}</p>
                <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-white/40">
                  <div
                    className="h-full bg-brand transition-[width]"
                    style={{ width: `${slot.progress}%` }}
                  />
                </div>
              </div>
            )}
            {slot.failed && (
              <p className="absolute inset-x-0 bottom-0 bg-negative px-1.5 py-1 text-micro text-white">
                {labels.failed}
              </p>
            )}
            {slot.progress === null && (
              <button
                type="button"
                aria-label={labels.remove}
                onClick={() => onRemove(slot.key)}
                className="absolute top-1 right-1 inline-flex size-6 items-center justify-center rounded-full bg-ink/70 text-surface"
              >
                <Icon name="x" size={12} />
              </button>
            )}
          </div>
        ))}
        {canAdd && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex size-20 flex-col items-center justify-center gap-1 rounded-xl border-[1.5px] border-dashed border-brand-600 bg-brand-soft text-caption font-semibold text-brand-ink"
          >
            <Icon name="camera" size={18} />
            {labels.add}
          </button>
        )}
      </div>
    </section>
  );
}

/** The rate sheet (C26): stars, dish likes, text, photos, draft strip, refusal notice, Submit. */
export function RateSheet({
  title,
  subtitle,
  onClose,
  draft,
  block,
  food,
  delivery,
  dishes,
  dishesTitle,
  text,
  photos,
  editNote,
  submitLabel,
  canSubmit,
  submitting,
  draftHint,
  onSubmit,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  draft: ReactNode;
  block: { title: string; body: string } | null;
  food: ReactNode;
  delivery: ReactNode;
  dishes: ReactNode[];
  dishesTitle: string;
  text: {
    label: string;
    placeholder: string;
    value: string;
    maxLength: number;
    counter: string;
    onChange: (value: string) => void;
  };
  photos: ReactNode;
  editNote: string | null;
  submitLabel: string;
  canSubmit: boolean;
  submitting: boolean;
  draftHint: string | null;
  onSubmit: () => void;
}) {
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <div>
          {draftHint && <p className="mb-2 text-center text-caption text-ink-3">{draftHint}</p>}
          <Button full disabled={!canSubmit} busy={submitting} onClick={onSubmit}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="-mt-3 text-xs text-ink-2">{subtitle}</p>
        {draft}
        {block && (
          <Notice tone="negative" title={block.title}>
            {block.body}
          </Notice>
        )}
        {editNote && <p className="text-caption text-ink-3">{editNote}</p>}
        {food}
        {delivery}
        {dishes.length > 0 && (
          <section>
            <p className="text-sm font-semibold text-ink">{dishesTitle}</p>
            <div className="mt-1 divide-y divide-hair">{dishes}</div>
          </section>
        )}
        <label className="block">
          <span className="text-sm font-semibold text-ink">{text.label}</span>
          <textarea
            value={text.value}
            rows={3}
            maxLength={text.maxLength}
            placeholder={text.placeholder}
            onChange={(event) => text.onChange(event.target.value)}
            className="mt-2 w-full resize-none rounded-field bg-surface px-3 py-2.5 text-sm text-ink shadow-[inset_0_0_0_1px_var(--color-field)] placeholder:text-ink-3 focus:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)] focus:outline-none"
          />
          <span className="block text-right text-caption text-ink-3 tabular">{text.counter}</span>
        </label>
        {photos}
      </div>
    </Sheet>
  );
}

/** Public hygiene badge (green ≥ 90, amber 75–89; hidden below). */
export function HygieneBadge({ tone, label }: { tone: 'green' | 'amber'; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-semibold ${
        tone === 'green' ? 'bg-positive-bg text-positive' : 'bg-warning-bg text-warning'
      }`}
    >
      <Icon name="shield" size={12} />
      {label}
    </span>
  );
}

/** Rating summary: big average (or "New"), count, 5→1 distribution bars, hygiene badge. */
export function RatingSummaryCard({
  title,
  average,
  isNew,
  newLabel,
  newBody,
  countText,
  distribution,
  hygiene,
  action,
}: {
  title: string;
  average: string | null;
  isNew: boolean;
  newLabel: string;
  newBody: string;
  countText: string;
  distribution: number[];
  hygiene: ReactNode;
  action?: ReactNode;
}) {
  const max = Math.max(1, ...distribution);
  return (
    <section className="rounded-sheet bg-surface p-4 shadow-[inset_0_0_0_1px_var(--color-hair)]">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-ink">{title}</h2>
        {hygiene}
      </div>
      {isNew || average === null ? (
        <div className="mt-3 flex items-center gap-3">
          <span className="rounded-lg bg-brand px-2.5 py-1 text-sm font-bold text-on-brand">
            {newLabel}
          </span>
          <p className="text-xs text-ink-2">{newBody}</p>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-4">
          <div className="flex w-20 shrink-0 flex-col items-center">
            <span className="inline-flex items-center gap-1 text-headline font-bold text-ink tabular">
              {average}
              <span className="text-positive">
                <Icon name="star" size={18} />
              </span>
            </span>
            <span className="text-center text-caption text-ink-3">{countText}</span>
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            {distribution.map((count, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="w-3 text-caption text-ink-2 tabular">{5 - index}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-tile">
                  <div
                    className="h-full rounded-full bg-positive"
                    style={{ width: `${Math.round((count / max) * 100)}%` }}
                  />
                </div>
                <span className="w-8 text-right text-caption text-ink-3 tabular">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {action && <div className="mt-3">{action}</div>}
    </section>
  );
}

export interface ReviewCardView {
  id: string;
  author: string;
  when: string;
  stars: number;
  starsLabel: string;
  text: string | null;
  textLines: number;
  photos: { id: string; url: string }[];
  liked: string | null;
  reply: { title: string; text: string } | null;
}

const CLAMP: Record<number, string> = { 1: 'line-clamp-1', 2: 'line-clamp-2', 3: 'line-clamp-3' };
const TEXT_HEIGHT: Record<number, string> = { 1: 'h-5', 2: 'h-10', 3: 'h-15' };

/** Public review card: avatar initial, name, date, star chip, text, photos, liked, reply. */
export function PublicReviewCard({
  review,
  photoLabel,
  onPhoto,
}: {
  review: ReviewCardView;
  photoLabel: string;
  onPhoto: (url: string) => void;
}) {
  return (
    <div className="pb-3">
      <article className="rounded-sheet bg-surface p-4 shadow-[inset_0_0_0_1px_var(--color-hair)]">
        <header className="flex h-11 items-center gap-3">
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-bold text-brand-ink">
            {review.author.slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{review.author}</p>
            <p className="text-caption text-ink-3">{review.when}</p>
          </div>
          <span
            aria-label={review.starsLabel}
            className={`inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold text-white tabular ${
              review.stars >= 3 ? 'bg-positive' : review.stars === 2 ? 'bg-warning' : 'bg-negative'
            }`}
          >
            {review.stars}
            <Icon name="star" size={11} />
          </span>
        </header>
        {review.text && review.textLines > 0 && (
          <p
            className={`mt-2 overflow-hidden text-sm text-ink ${CLAMP[review.textLines] ?? 'line-clamp-3'} ${TEXT_HEIGHT[review.textLines] ?? 'h-15'}`}
          >
            {review.text}
          </p>
        )}
        {review.photos.length > 0 && (
          <div className="mt-2 flex h-16 gap-2">
            {review.photos.map((photo) => (
              <button
                key={photo.id}
                type="button"
                aria-label={photoLabel}
                onClick={() => onPhoto(photo.url)}
                className="size-16 overflow-hidden rounded-lg bg-tile"
              >
                <img src={photo.url} alt="" loading="lazy" className="size-full object-cover" />
              </button>
            ))}
          </div>
        )}
        {review.liked && (
          <p className="mt-2 flex h-5 items-center gap-1 truncate text-caption text-positive">
            <Thumb size={12} />
            <span className="truncate">{review.liked}</span>
          </p>
        )}
        {review.reply && (
          <div className="mt-2 h-19 overflow-hidden rounded-xl bg-tile px-3 py-2.5">
            <p className="text-caption font-semibold text-ink-2">{review.reply.title}</p>
            <p className="line-clamp-2 text-xs text-ink">{review.reply.text}</p>
          </div>
        )}
      </article>
    </div>
  );
}

/** Sort pills for the reviews list (black = selected). */
export function SortPills<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={`inline-flex h-8 shrink-0 items-center rounded-full px-3.5 text-xs ${
            value === option.value
              ? 'bg-ink font-medium text-surface'
              : 'bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-control)]'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Full-screen photo viewer (tap anywhere to close). */
export function PhotoViewer({
  url,
  closeLabel,
  onClose,
}: {
  url: string;
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-70 flex items-center justify-center bg-scrim p-4"
      role="dialog"
      aria-label={closeLabel}
    >
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute inset-0"
      />
      <img src={url} alt="" className="relative max-h-full max-w-full rounded-xl object-contain" />
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute top-4 right-4 inline-flex size-10 items-center justify-center rounded-full bg-ink/80 text-surface"
      >
        <Icon name="x" size={18} />
      </button>
    </div>
  );
}

export interface MyReviewCardView {
  id: string;
  outletName: string;
  meta: string;
  food: { label: string; stars: number; aria: string };
  delivery: { label: string; stars: number; aria: string } | null;
  text: string | null;
  textLines: number;
  photos: { id: string; url: string }[];
  status: { text: string; tone: 'warning' | 'negative' | 'neutral' } | null;
  reply: { title: string; text: string } | null;
  edit: { label: string; note: string } | null;
}

/** My review card (Account › My reviews). */
export function MyReviewCard({
  review,
  onEdit,
  onOpen,
}: {
  review: MyReviewCardView;
  onEdit: () => void;
  onOpen: () => void;
}) {
  const statusTone =
    review.status?.tone === 'negative'
      ? 'bg-negative-bg text-negative'
      : review.status?.tone === 'warning'
        ? 'bg-warning-bg text-warning'
        : 'bg-tile text-ink-2';
  return (
    <div className="pb-3">
      <article className="rounded-sheet bg-surface p-4 shadow-[inset_0_0_0_1px_var(--color-hair)]">
        <button
          type="button"
          onClick={onOpen}
          className="flex h-11 w-full items-center gap-2 text-left"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-ink">{review.outletName}</p>
            <p className="truncate text-caption text-ink-3">{review.meta}</p>
          </div>
          <Icon name="chevron-right" size={16} />
        </button>
        <div className="flex h-7 items-center gap-4 text-caption text-ink-2">
          <span className="inline-flex items-center gap-1.5">
            {review.food.label}
            <StarRow value={review.food.stars} label={review.food.aria} size={12} />
          </span>
          {review.delivery && (
            <span className="inline-flex items-center gap-1.5">
              {review.delivery.label}
              <StarRow value={review.delivery.stars} label={review.delivery.aria} size={12} />
            </span>
          )}
        </div>
        {review.text && review.textLines > 0 && (
          <p
            className={`mt-2 overflow-hidden text-sm text-ink ${CLAMP[review.textLines] ?? 'line-clamp-3'} ${TEXT_HEIGHT[review.textLines] ?? 'h-15'}`}
          >
            {review.text}
          </p>
        )}
        {review.photos.length > 0 && (
          <div className="mt-2 flex h-16 gap-2">
            {review.photos.map((photo) => (
              <img
                key={photo.id}
                src={photo.url}
                alt=""
                loading="lazy"
                className="size-16 rounded-lg bg-tile object-cover"
              />
            ))}
          </div>
        )}
        {review.status && (
          <p
            className={`mt-2 flex h-5 items-center truncate rounded-md px-2 text-caption font-medium ${statusTone}`}
          >
            {review.status.text}
          </p>
        )}
        {review.reply && (
          <div className="mt-2 h-19 overflow-hidden rounded-xl bg-tile px-3 py-2.5">
            <p className="text-caption font-semibold text-ink-2">{review.reply.title}</p>
            <p className="line-clamp-2 text-xs text-ink">{review.reply.text}</p>
          </div>
        )}
        {review.edit && (
          <div className="mt-2 flex h-9 items-center justify-between gap-3">
            <p className="min-w-0 truncate text-caption text-ink-3">{review.edit.note}</p>
            <button
              type="button"
              onClick={onEdit}
              className="shrink-0 text-sm font-semibold text-brand-ink"
            >
              {review.edit.label}
            </button>
          </div>
        )}
      </article>
    </div>
  );
}

/** One order waiting for a rating: outlet, order no + rate-by, yellow "Rate". */
export function PendingRateRow({
  outletName,
  meta,
  actionLabel,
  onRate,
}: {
  outletName: string;
  meta: string;
  actionLabel: string;
  onRate: () => void;
}) {
  return (
    <div className="flex h-16 items-center gap-3 px-4">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
        <Icon name="star" size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{outletName}</p>
        <p className="truncate text-caption text-ink-3">{meta}</p>
      </div>
      <Button size="sm" onClick={onRate}>
        {actionLabel}
      </Button>
    </div>
  );
}

/** "Rate your orders" strip (Home + Account) while orders wait for a rating. */
export function RateStrip({
  title,
  body,
  actionLabel,
  onRate,
  onOpenAll,
}: {
  title: string;
  body: string;
  actionLabel: string;
  onRate: () => void;
  onOpenAll: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-sheet bg-brand-soft px-4 py-3">
      <button
        type="button"
        onClick={onOpenAll}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand">
          <Icon name="star" size={20} />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-bold text-ink">{title}</span>
          <span className="block truncate text-xs text-ink-2">{body}</span>
        </span>
      </button>
      <Button size="sm" onClick={onRate}>
        {actionLabel}
      </Button>
    </div>
  );
}

/** Order page: "How was your order?" card, or the rating you gave with Edit. */
export function OrderRatingCard({
  title,
  body,
  actionLabel,
  onAction,
  stars,
}: {
  title: string;
  body: string;
  actionLabel: string | null;
  onAction: () => void;
  stars: ReactNode;
}) {
  return (
    <section className="rounded-sheet bg-surface p-4 shadow-[inset_0_0_0_1px_var(--color-hair)]">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
          <Icon name="star" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-ink">{title}</p>
          <p className="text-xs text-ink-2">{body}</p>
          {stars && <div className="mt-1.5">{stars}</div>}
        </div>
        {actionLabel && (
          <Button size="sm" tone={stars ? 'outline' : 'brand'} onClick={onAction}>
            {actionLabel}
          </Button>
        )}
      </div>
    </section>
  );
}
